import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ShoppingCart, Star, Truck, Shield, RotateCcw, X } from 'lucide-react'
import { productService, simpleAttributesService } from '../services/api'
import { useCart } from '../contexts/CartContext'
import LoadingSpinner from '../components/LoadingSpinner'
import ContactButtons from '../components/ContactButtons'
import Currency from '../components/Currency'
import ProductCard from '../components/ProductCard'
import MetaTags from '../components/MetaTags'

const ProductDetail = () => {
  const { id } = useParams()
  const { addToCart, loading: cartLoading } = useCart()
  const [selectedVariation, setSelectedVariation] = useState(null)
  const [selectedAttributes, setSelectedAttributes] = useState({})
  const [selectedImage, setSelectedImage] = useState(null)

  // Scroll to top when component mounts or id changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [id])

  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productService.getProduct(id),
    enabled: !!id,
  })

  // Fetch product-specific attributes and their variations
  const { data: attributes = [] } = useQuery({
    queryKey: ['productAttributes', id],
    queryFn: () => productService.getProductAttributes(id),
    enabled: !!id,
  })

  // Fetch related products
  const { data: relatedProducts = [], isLoading: relatedLoading } = useQuery({
    queryKey: ['relatedProducts', id],
    queryFn: () => productService.getRelatedProducts(id, 6),
    enabled: !!id,
  })

  const handleAddToCart = () => {
    addToCart(product.id, 1, selectedAttributes)
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSpinner size="xl" className="py-12" />
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <p className="text-gray-600">The product you're looking for doesn't exist.</p>
        </div>
      </div>
    )
  }

  // Calculate price including attribute variations
  const calculateTotalPrice = () => {
    const basePrice = product.sale_price || product.price
    let additionalPrice = 0
    
    // Add prices from selected attributes
    if (attributes && attributes.length > 0) {
      attributes.forEach(attribute => {
        const selectedVariation = selectedAttributes[attribute.id]
        if (selectedVariation) {
          const variation = attribute.variations.find(v => v.id === selectedVariation.id)
          if (variation && variation.additional_price) {
            additionalPrice += parseFloat(variation.additional_price)
          }
        }
      })
    }
    
    return parseFloat(basePrice) + additionalPrice
  }

  const displayPrice = calculateTotalPrice()
  const originalPrice = product.sale_price ? parseFloat(product.price) : null
  const discount = originalPrice ? Math.round(((originalPrice - displayPrice) / originalPrice) * 100) : 0

  const handleVariationSelect = (attributeId, variationId, variationTitle) => {
    setSelectedAttributes(prev => ({
      ...prev,
      [attributeId]: { id: variationId, title: variationTitle }
    }))
  }

  // Check if all required attributes are selected
  const areRequiredAttributesSelected = () => {
    if (!attributes || attributes.length === 0) return true
    
    return attributes.every(attribute => {
      // If the attribute has variations, user must select one
      if (attribute.variations && attribute.variations.length > 0) {
        return selectedAttributes[attribute.id] !== undefined
      }
      return true
    })
  }

  const canAddToCart = product.in_stock && areRequiredAttributesSelected()


  return (
    <>
      <MetaTags 
        title={`${product.name} - سبحات`}
        description={product.description || `اشتري ${product.name} بأفضل سعر من سبحات - نخبة المسابيح`}
        keywords={`${product.name}, مسابيح, سبح, تسبيح, إسلامي, كويت`}
        image={product.images && product.images.length > 0 ? `/uploads/products/${product.images[0].filename}` : (product.featured_image ? `/uploads/products/${product.featured_image}` : null)}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Product Images */}
        <div>
          <div className="aspect-w-1 aspect-h-1 bg-gray-200 rounded-lg overflow-hidden mb-4">
            {product.images && product.images.length > 0 ? (
              <img
                src={`/uploads/products/${product.images[0].filename}`}
                alt={product.name}
                className="w-full h-96 object-cover"
              />
            ) : product.featured_image ? (
              <img
                src={`/uploads/products/${product.featured_image}`}
                alt={product.name}
                className="w-full h-96 object-cover"
              />
            ) : (
              <div className="w-full h-96 bg-gray-300 flex items-center justify-center">
                <span className="text-subtle">No Image</span>
              </div>
            )}
          </div>

          {/* Gallery */}
          {product.images && product.images.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {product.images.map((image, index) => (
                <div key={image.id || index} className="aspect-w-1 aspect-h-1 bg-gray-200 rounded overflow-hidden relative">
                  {(image.is_featured || index === 0) && (
                    <div className="absolute top-1 left-1 bg-yellow-500 text-white text-xs px-1 py-0.5 rounded z-10">
                      ⭐
                    </div>
                  )}
                  <img
                    src={`/uploads/products/${image.filename || image}`}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-full object-cover cursor-pointer hover:opacity-75"
                    onClick={() => setSelectedImage(`/uploads/products/${image.filename || image}`)}
                  />
                </div>
              ))}
            </div>
          )}
          
          {/* Fallback for old gallery field */}
          {(!product.images || product.images.length === 0) && product.gallery && product.gallery.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {product.gallery.map((image, index) => (
                <div key={index} className="aspect-w-1 aspect-h-1 bg-gray-200 rounded overflow-hidden relative">
                  {index === 0 && (
                    <div className="absolute top-1 left-1 bg-yellow-500 text-white text-xs px-1 py-0.5 rounded z-10">
                      ⭐
                    </div>
                  )}
                  <img
                    src={`/uploads/products/${image}`}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-full object-cover cursor-pointer hover:opacity-75"
                    onClick={() => setSelectedImage(`/uploads/products/${image}`)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div>
          <div className="mb-4">
            {product.category_name && (
              <p className="text-sm text-primary-600 font-medium mb-2">
                {product.category_name}
              </p>
            )}
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {product.name}
            </h1>
            
           
            
            {/* Price */}
            <div className="flex items-center space-x-4 mb-6">
              <span className="text-3xl font-bold text-gray-900">
                <Currency amount={displayPrice} />
              </span>
              {originalPrice && (
                <>
                  <span className="text-xl text-muted line-through">
                    <Currency amount={originalPrice} />
                  </span>
                  <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm font-medium">
                    {discount}% OFF
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
            <p className="text-gray-600 leading-relaxed">
              {product.description || product.short_description || 'No description available.'}
            </p>
          </div>

          {/* Stock Status */}
          <div className="mb-6">
            <div className="flex items-center space-x-2">
              <div
                className={`w-3 h-3 rounded-full ${
                  product.in_stock ? 'bg-green-500' : 'bg-red-500'
                }`}
              />
              <span className={`font-medium ${
                product.in_stock ? 'text-green-600' : 'text-red-600'
              }`}>
                {product.in_stock ? 'In Stock' : 'Out of Stock'}
              </span>
              {product.manage_stock && (
                <span className="text-sm text-muted">
                  ({product.stock_quantity} available)
                </span>
              )}
            </div>
          </div>

          {/* Attribute Selection */}
          {attributes && attributes.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">اختر الخصائص</h3>
              {attributes.map((attribute) => (
                <div key={attribute.id} className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    {attribute.name}
                    {attribute.variations && attribute.variations.length > 0 && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {attribute.variations?.map((variation) => (
                      <button
                        key={variation.id}
                        onClick={() => handleVariationSelect(attribute.id, variation.id, variation.title)}
                        className={`px-4 py-2 border rounded-lg text-sm font-medium transition-all duration-200 ${
                          selectedAttributes[attribute.id]?.id === variation.id
                            ? 'border-primary-600 bg-primary-50 text-primary-700'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        {variation.title}
                        {variation.additional_price > 0 && (
                          <span className="ml-1 text-xs text-green-600">
                            (+<Currency amount={variation.additional_price} />)
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                  {attribute.variations && attribute.variations.length > 0 && !selectedAttributes[attribute.id] && (
                    <p className="text-sm text-red-500 mt-2">يرجى اختيار {attribute.name}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Contact Buttons */}
          <ContactButtons product={product} />

          {/* Add to Cart */}
          <div className="mb-8">
            {!areRequiredAttributesSelected() && (
              <p className="text-sm text-red-500 mb-3">يرجى اختيار جميع الخصائص المطلوبة</p>
            )}
            <button
              onClick={handleAddToCart}
              disabled={cartLoading || !canAddToCart}
              className={`w-full btn btn-primary btn-lg flex items-center justify-center space-x-2 ${
                cartLoading || !canAddToCart
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
            >
              <ShoppingCart className="h-5 w-5" />
              <span>
                {cartLoading ? 'Adding to Cart...' : 'Add to Cart'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Related Products */}
      {relatedProducts && relatedProducts.length > 0 && (
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">منتجات ذات صلة</h2>
          {relatedLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          )}
        </div>
      )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setSelectedImage(null)}>
          <div className="relative max-w-4xl max-h-screen p-4">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 z-10"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={selectedImage}
              alt="Product enlarged"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  )
}

export default ProductDetail
